/**
 * AskPurple Offline Intelligence Engine
 * 
 * Provides robust, grade-aware, ecosystem-aware AI assistance when the backend
 * is unavailable or running in standalone frontend mode.
 */
import { SITE } from "../constants";

export const GOLD_EXPERTS = [
  {
    id: "exp-anjali-mehra",
    name: "Dr. Anjali Mehra",
    title: "Senior Career Counsellor",
    vertical: "counselling",
    venture: "CounselNest",
    tier: "gold",
    tags: ["career", "stream selection", "class 10", "class 12", "confused", "psychometric", "counselling"],
    bio: "15+ years guiding students through stream selection, career discovery, and parental conflicts. ICCC certified.",
    location: "Bengaluru, India",
    languages: ["English", "Hindi", "Kannada"],
    image: "https://images.unsplash.com/photo-1604177091072-b7b677a077f6?auto=format&fit=crop&w=600&q=70",
    profile_url: "https://counselnest.in/counsellor/anjali-mehra",
  },
  {
    id: "exp-rohan-iyer",
    name: "Dr. Rohan Iyer",
    title: "Psychometric & DMIT Specialist",
    vertical: "counselling",
    venture: "CounselNest",
    tier: "gold",
    tags: ["psychometric", "dmit", "assessment", "aptitude", "personality", "test"],
    bio: "IIT-B alumnus. Conducts scientific psychometric + DMIT assessments and interprets results for families.",
    location: "Mumbai, India",
    languages: ["English", "Hindi", "Marathi"],
    image: "https://images.unsplash.com/photo-1637589308599-3478cc55510d?auto=format&fit=crop&w=600&q=70",
    profile_url: "https://counselnest.in/counsellor/rohan-iyer",
  },
  {
    id: "exp-kavya-reddy",
    name: "Dr. Kavya Reddy",
    title: "Child Career Psychologist",
    vertical: "counselling",
    venture: "CounselNest",
    tier: "gold",
    tags: ["dmit", "child", "early career", "class 9", "class 10", "parents", "psychology"],
    bio: "Child psychology + DMIT specialist for students aged 12-16. Works closely with parents.",
    location: "Hyderabad, India",
    languages: ["English", "Telugu", "Hindi"],
    image: "https://images.unsplash.com/photo-1571260899304-425eee4c7efc?auto=format&fit=crop&w=600&q=70",
    profile_url: "https://counselnest.in/counsellor/kavya-reddy",
  },
  {
    id: "exp-priya-nair",
    name: "Priya Nair",
    title: "Overseas Admissions Lead",
    vertical: "admissions",
    venture: "AdmissionsNest",
    tier: "gold",
    tags: ["overseas", "study abroad", "us", "uk", "canada", "sop", "ielts", "gre", "scholarship", "visa", "admissions"],
    bio: "Has placed 400+ students in US, UK, Canada, Australia. SOP review, scholarship strategy, visa prep.",
    location: "Bengaluru, India",
    languages: ["English", "Hindi", "Malayalam"],
    image: "https://images.unsplash.com/photo-1551731409-43eb3e517a1a?auto=format&fit=crop&w=600&q=70",
    profile_url: "https://admissionsnest.in/counsellor/priya-nair",
  },
  {
    id: "exp-arjun-kapoor",
    name: "Arjun Kapoor",
    title: "India Admissions Strategist",
    vertical: "admissions",
    venture: "AdmissionsNest",
    tier: "gold",
    tags: ["india", "jee", "neet", "cuet", "engineering", "medical", "bba", "ba", "college", "admissions"],
    bio: "Specialist in JEE/NEET/CUET counselling, college shortlisting, branch selection, and admissions strategy.",
    location: "Delhi, India",
    languages: ["English", "Hindi", "Punjabi"],
    image: "https://images.unsplash.com/photo-1623303366639-0e330d7c3d9f?auto=format&fit=crop&w=600&q=70",
    profile_url: "https://admissionsnest.in/counsellor/arjun-kapoor",
  },
  {
    id: "exp-sandeep-rao",
    name: "Sandeep Rao",
    title: "Commerce & CA/CS Mentor",
    vertical: "admissions",
    venture: "AdmissionsNest",
    tier: "gold",
    tags: ["commerce", "ca", "cs", "cma", "bcom", "online degree", "professional", "finance"],
    bio: "Mentors commerce-stream students for CA, CS, CMA, B.Com, and emerging online degree pathways.",
    location: "Chennai, India",
    languages: ["English", "Tamil", "Hindi"],
    image: "https://images.unsplash.com/photo-1686624386665-4cd01b96d0f6?auto=format&fit=crop&w=600&q=70",
    profile_url: "https://admissionsnest.in/counsellor/sandeep-rao",
  },
  {
    id: "exp-vikram-sharma",
    name: "Vikram Sharma",
    title: "JEE Mathematics Tutor",
    vertical: "tutoring",
    venture: "TutorsNest",
    tier: "gold",
    tags: ["maths", "math", "mathematics", "jee", "iit", "class 11", "class 12", "engineering", "tutor", "tutoring"],
    bio: "IIT-Delhi alumnus, 10+ years coaching JEE Mains & Advanced Mathematics. AIR <100 mentees: 30+.",
    location: "Online / Delhi",
    languages: ["English", "Hindi"],
    image: "https://images.unsplash.com/photo-1637589316488-6d4c41b335cd?auto=format&fit=crop&w=600&q=70",
    profile_url: "https://tutorsnest.in/tutor/vikram-sharma",
  },
  {
    id: "exp-meera-iyer",
    name: "Meera Iyer",
    title: "IELTS & Spoken English Coach",
    vertical: "tutoring",
    venture: "TutorsNest",
    tier: "gold",
    tags: ["ielts", "english", "spoken english", "toefl", "communication", "soft skills", "tutor", "tutoring"],
    bio: "Cambridge CELTA certified. Helps students score 7.5+ in IELTS and build interview-ready communication.",
    location: "Online / Pune",
    languages: ["English", "Hindi", "Marathi"],
    image: "https://images.unsplash.com/photo-1522661067900-ab829854a57f?auto=format&fit=crop&w=600&q=70",
    profile_url: "https://tutorsnest.in/tutor/meera-iyer",
  },
];

const SESSION_STORAGE_KEY = "askpurple_offline_sessions";

function getStoredSessions() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return {};
    const raw = window.localStorage.getItem(SESSION_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveStoredSessions(sessions) {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessions));
    }
  } catch (e) {
    console.warn("Failed to persist offline chat session", e);
  }
}

/**
 * Match relevant experts based on query text and vertical
 */
export function matchExperts(queryText, vertical = "general") {
  const q = queryText.toLowerCase();
  const scored = GOLD_EXPERTS.map((exp) => {
    let score = 0;
    if (vertical !== "general" && exp.vertical === vertical) score += 4;
    for (const tag of exp.tags) {
      if (q.includes(tag)) score += 3;
    }
    if (q.includes(exp.name.toLowerCase())) score += 10;
    if (q.includes(exp.venture.toLowerCase())) score += 2;
    return { expert: exp, score };
  });

  scored.sort((a, b) => b.score - a.score);
  return scored.filter((s) => s.score > 0).slice(0, 2).map((s) => s.expert);
}

/**
 * Generate an ecosystem-aware response for AskPurple
 */
export function generateResponse({ message, profile, contextVertical = "general", sessionId }) {
  const sid = sessionId || `session-${Date.now()}`;
  const sessions = getStoredSessions();
  const history = sessions[sid] || [];

  const text = message.trim().toLowerCase();
  const studentName = profile?.name ? profile.name.trim() : "";
  const nameSalutation = studentName ? ` ${studentName}` : "";
  const grade = profile?.level ? profile.level.replace("_", " ") : "";
  const curriculum = profile?.curriculum ? profile.curriculum.toUpperCase() : "";
  const primaryNeed = profile?.need || contextVertical;

  let reply = "";
  let targetVertical = primaryNeed;

  // 1. Stream Selection & Career Counselling (Prioritize career confusion, stream, assessments)
  if (/(stream|confused|confusion|career|psychometric|dmit|aptitude|class 10|class 12|science or commerce|arts or commerce|pcm|pcb|which career|subject choice)/i.test(text)) {
    targetVertical = "counselling";
    reply = `Hello${nameSalutation}! Making the right stream or career choice should always be evidence-led rather than based on peer pressure. At CounselNest, we administer validated scientific psychometric tests and DMIT evaluations to map your innate cognitive strengths, interests, and personality.\n\nNext step: Take a psychometric assessment and schedule a 1-on-1 session with a certified counsellor on CounselNest (https://counselnest.in).`;
  }
  // 2. Study Abroad / Overseas Admissions & Scholarships
  else if (/(abroad|overseas|foreign|\bus\b|\busa\b|\buk\b|\bcan\b|canada|australia|germany|gre|toefl|ielts|sop|visa|scholarship|foreign university)/i.test(text)) {
    targetVertical = "admissions";
    reply = `Hello${nameSalutation}! For studying abroad, planning your profile 10-12 months in advance is essential. Through AdmissionsNest, we guide you through university discovery, SOP writing, scholarship shortlisting, and visa documentation. We recommend reviewing top institutions that match your target budget and test timeline.\n\nNext step: Visit AdmissionsNest (https://admissionsnest.in) to explore overseas programs and book an admissions strategist.`;
  }
  // 3. Indian Admissions / Exams (JEE, NEET, CUET, College Admission)
  else if (/(jee|iit|neet|cuet|engineering|medical|btech|bba|mbbs|college admission|counselling round|cutoff|entrance exam)/i.test(text)) {
    targetVertical = "admissions";
    reply = `Hello${nameSalutation}! Navigating college admissions in India requires a clear balance between entrance scores, branch preference, and institutional placement history. AdmissionsNest provides structured branch-versus-college matrix evaluation and counselling assistance for JEE, NEET, and CUET.\n\nNext step: Explore college cutoffs and direct application guidance on AdmissionsNest (https://admissionsnest.in).`;
  }
  // 4. Tutoring / Subjects / Coaching / Learning
  else if (/(tutor|tutoring|math|maths|physics|chemistry|biology|teacher|class|syllabus|ncert|homework|coaching|lesson|tuition)/i.test(text)) {
    targetVertical = "tutoring";
    const bookMention = profile?.textbook_prefs ? ` We can align sessions directly with your preferred textbooks (${profile.textbook_prefs}).` : "";
    reply = `Hello${nameSalutation}! Targeted 1-on-1 tutoring helps bridge specific conceptual gaps before board and competitive exams.${bookMention} TutorsNest provides curated, verified subject tutors who focus on outcome tracking and personalized revision.\n\nNext step: Connect with specialized subject educators on TutorsNest (https://tutorsnest.in).`;
  }
  // 5. Fees, Pricing & Costs
  else if (/(fee|fees|cost|price|pricing|charge|how much|package)/i.test(text)) {
    reply = `Hello${nameSalutation}! At Crescet Edustreams, our pricing is transparent and tiered based on student requirements — from initial diagnostic sessions and individual subject tutoring hours to complete 12-month admission and psychometric packages.\n\nDirect Support:\nWhatsApp: ${SITE.whatsappDisplay}\nEmail: ${SITE.email}\n\nNext step: Reach out to our advisory team via WhatsApp to get the current package fee schedule.`;
  }
  // 6. Parents & Child Guidance
  else if (/(parent|child|daughter|son|kid|family)/i.test(text)) {
    targetVertical = "counselling";
    reply = `Hello${nameSalutation}! Parents are an integral partner in every educational milestone at Crescet Edustreams. Our certified counsellors host collaborative student-parent sessions to discuss assessment reports, college shortlists, and realistic career trajectories without familial pressure.\n\nNext step: Schedule a parent-student consultation on CounselNest (https://counselnest.in).`;
  }
  // 7. CounselNest Specific
  else if (/counselnest/i.test(text)) {
    targetVertical = "counselling";
    reply = `CounselNest (https://counselnest.in) is our dedicated career intelligence platform. It provides scientific psychometric evaluations, DMIT brain-mapping tests, and 1-on-1 sessions with ICCC-certified counsellors across 50+ career clusters.`;
  }
  // 8. AdmissionsNest Specific
  else if (/admissionsnest/i.test(text)) {
    targetVertical = "admissions";
    reply = `AdmissionsNest (https://admissionsnest.in) is our comprehensive higher-education platform covering both domestic Indian admissions (JEE/NEET/CUET/Private universities) and overseas study pathways across the US, UK, Canada, Australia, and Europe.`;
  }
  // 9. TutorsNest Specific
  else if (/tutorsnest/i.test(text)) {
    targetVertical = "tutoring";
    reply = `TutorsNest (https://tutorsnest.in) is our personalized 1-on-1 tutoring network pairing school and college learners with top-percentile mentors for board mastery (CBSE, ICSE, IB, Cambridge) and competitive entrances.`;
  }
  // 10. Greetings
  else if (/^(hi|hello|hey|greetings|good\s*(morning|afternoon|evening)|yo\b)/i.test(text)) {
    if (grade && curriculum) {
      reply = `Hello${nameSalutation}! I'm AskPurple, your study buddy across Crescet Edustreams. I see you're in ${grade} (${curriculum}). How can I help you today with admissions, career counselling, or targeted tutoring?`;
    } else {
      reply = `Hello${nameSalutation}! I'm AskPurple, your connected guide across Crescet Edustreams. Are you looking for guidance on college admissions, career counselling & psychometrics, or finding a tutor?`;
    }
  }
  // 11. Direct Contact / WhatsApp / Email Support
  else if (/(whatsapp|email|contact|phone|call|helpline|support|reach out|number)/i.test(text)) {
    reply = `Hello${nameSalutation}! You can reach our student and parent advisory team directly at Crescet Edustreams:\n\nDirect Support:\nWhatsApp: ${SITE.whatsappDisplay}\nEmail: ${SITE.email}\n\nOur team is here to assist with admissions, career counselling, and 1-on-1 tutoring.`;
  }
  // 12. About Crescet Edustreams Ecosystem
  else if (/(crescet|ecosystem|ventures|about|partner|what do you do)/i.test(text)) {
    reply = `Crescet Edustreams is a unified education ecosystem connecting three specialized platforms: AdmissionsNest for university admissions, CounselNest for scientific career counselling & assessments, and TutorsNest for targeted 1-on-1 learning. AskPurple bridges these verticals so students never have to re-explain their journey.\n\nNext step: Explore our connected platforms or partner with our advisory network.`;
  }
  // 13. General queries / Fallback
  else {
    reply = `Hello${nameSalutation}! As your Crescet Edustreams advisor, I can help connect your academic goals with AdmissionsNest for college entry, CounselNest for aptitude and career assessments, or TutorsNest for subject tutoring. Could you tell me a little more about your target goal or timeline?\n\nNext step: Share your target goal or explore CounselNest & AdmissionsNest.`;
  }

  // Ensure direct contact details (WhatsApp & Email) are appended to all responses
  const contactFooter = `\n\nDirect Support:\nWhatsApp: ${SITE.whatsappDisplay}\nEmail: ${SITE.email}`;
  if (!reply.includes(SITE.email)) {
    reply = `${reply.trim()}${contactFooter}`;
  }

  // Update session history
  history.push({ role: "user", text: message, created_at: new Date().toISOString() });
  history.push({ role: "assistant", text: reply, created_at: new Date().toISOString() });
  sessions[sid] = history;
  saveStoredSessions(sessions);

  return {
    session_id: sid,
    reply,
    recommended_experts: [],
  };
}

/**
 * Fetch mock chat history
 */
export function getStoredHistory(sessionId) {
  const sessions = getStoredSessions();
  return sessions[sessionId] || [];
}
